import React from "react";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import { connect } from "react-redux";
import { Formik } from "formik";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import { DocumentType } from "constants/TemplateEditor";
import Common from "us.common";
import { BoxIcons, FileTextOutlined, IconTypes } from "us.icons";
import { navigateToEditor } from "components/EditorHome/Functions";
import { filterRulesByTemplateId, handleFilterChange } from "./Functions";
import { ENTITY_GROUPS_OPTIONS, EntityTypes } from "./Constants";
import { getDocumentFormatOptions } from "functions/EditorHome";

/**
 * @description - Global rule setup drawer Content View
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/PVejV41r
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 28/06/2023
 */
const GlobalRuleSetup: React.FC<any> = (props) => {
  const { t } = useTranslation();
  const {
    templateRules,
    templates,
    selectTemplate,
    setProperties,
    documentTypes,
  } = props;

  const { $List, $Radio, $Search, $Select, $Skeleton } = Common.Components;
  const navigate = useNavigate();

  /**
   * @description handle navigation to templates
   * @param {any} templateId
   * @param {any} event
   * @returns {void}
   */
  const handleNavigation = (templateId: any, event: any): void => {
    const template = filterRulesByTemplateId(templateId, templates.data);

    if (template && templateId == 0) {
      // start with a new or copy of a template
      setProperties && setProperties(template[0]);
    }
    selectTemplate && selectTemplate({ templateId: templateId });

    template && navigateToEditor(navigate, template[0], event);
  };

  return (
    <Formik
      initialValues={{
        searchText: "",
        entityType: EntityTypes.ALL,
        documentType: DocumentType.ALL,
      }}
      enableReinitialize
      onSubmit={() => {}}
    >
      {({ values }: any) => (
        <div>
          <div className="check-template-rule">
            <div className="ctr-header">
              <div className="mb-3">
                <$Search
                  size="small"
                  name="searchText"
                  allowClear={true}
                  placeholder={t(
                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SEARCH_BY_CODE_NAME_DOCUMENT_ETC"
                  )}
                  className="ctr-search"
                />
              </div>
              <div className="d-flex align-items-start">
                <div>
                  <$Radio
                    size="small"
                    name="entityType"
                    radioButton="true"
                    optionValue="value"
                    optionText="label"
                    optionContent={(item: any, index: number) => item.label}
                    className="d-flex mb-3"
                    optionStyle="flex-fill text-center"
                    options={ENTITY_GROUPS_OPTIONS.map((option: any) => ({
                      ...option,
                      label: t(option.label),
                    }))}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </div>
                <div className="ml-3" style={{ marginTop: "-4px" }}>
                  <$Select
                    name="documentType"
                    formitem={{}}
                    size="small"
                    className="mr-2"
                    style={{ width: 135 }}
                    loading={documentTypes.loading}
                    disabled={documentTypes.loading}
                    options={[
                      {
                        label: t(
                          "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ALL_FORMATS"
                        ),
                        value: DocumentType.ALL,
                      },
                      ...getDocumentFormatOptions(documentTypes?.data),
                    ]}
                    defaultValue={DocumentType.ALL}
                    allOption={false}
                    hideSearch
                  />
                </div>
              </div>
            </div>
            <div className="ctr-body">
              <$Skeleton
                loading={templateRules.isLoading}
                active
                paragraph={{ rows: 2 }}
                className={templateRules.isLoading && "px-3"}
              >
                <$List
                  itemLayout="horizontal"
                  dataSource={handleFilterChange(values, templateRules.data)}
                  renderItem={(templateRule, index) => (
                    <$List.Item
                      key={templateRule.templateId.toString()}
                      className="ctr-list-item"
                    >
                      <$List.Item.Meta
                        className="align-items-start"
                        avatar={
                          <BoxIcons
                            type={IconTypes.BOX_ICON}
                            name={templateRule.outputFormat?.toLowerCase()}
                          />
                        }
                        title={
                          <a
                            target="_blank"
                            onClick={(e: any) =>
                              handleNavigation(templateRule.templateId, e)
                            }
                          >
                            {templateRule.templateName}{" "}
                            {values.entityType == EntityTypes.ALL && (
                              <span className="label">
                                [ {templateRule.group} ]
                              </span>
                            )}
                          </a>
                        }
                        description={templateRule.ruleExpressions?.map(
                          (expression, index) => {
                            return (
                              <div
                                key={expression + index}
                                className="d-flex align-items-start mt-2"
                              >
                                <span className="ctr-bullet">
                                  <FileTextOutlined />
                                </span>
                                <div
                                  className="ml-2"
                                  dangerouslySetInnerHTML={{
                                    __html: expression,
                                  }}
                                />
                              </div>
                            );
                          }
                        )}
                      />
                    </$List.Item>
                  )}
                />
              </$Skeleton>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { editorHome, templatesHistory } = state;
  const { templateRules } = editorHome;
  const { templates, documentTypes, templateCopy } = templatesHistory;
  return {
    templateRules,
    templates,
    documentTypes,
    templateCopy,
  };
};

const { pages, template } = Actions.templatesHistory;

const mapDispatchToProps = {
  selectTemplate: template.select,
  setProperties: pages.setProperties,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(GlobalRuleSetup);
